import React from 'react'
import createDebug from 'debug'
import { string } from 'prop-types'
import { Flex, Box, Heading } from 'rebass'
import { Link } from 'rebass'
import Rheostat from 'rheostat'
import NumberFormat from 'react-number-format'
import { Form } from '../../utils/style'
import { TabList, TabPanel, Tab, Tabs } from 'react-tabs'
import {
  TabWrapper,
  TabLabel,
  StaticLabel,
  Legend,
  RheostatSliderWithDragValue,
  Header
} from './components/Tabs'
import PCP from './components/PCP'
import HP from './components/HP'
import LoadingSpinner from './components/LoadingSpinner'
import { ResetButton, ResetButtonWrapper } from './components/ResetButton'
import ContentFinanceSummary from '../ContentFinanceSummary'
import ContentSection from '../ContentSection'
import ContentSectionHeader from '../ContentSectionHeader'
import ContentClickModal from '../ContentClickModal'
import { request } from 'graphql-request'
import debounce from 'debounce-promise'
import memoize from 'promise-memoize'
import { trackEvent } from '../../../lib/tracker'
import { Legal } from './components/Legal'

const debug = createDebug('finance-calc')

const search = memoize(
  async (type, registration, deposit, annualMileage, termLength) => {
    termLength = Math.round(termLength)
    deposit = deposit && deposit.length > 0 ? Math.round(deposit[0]) : undefined
    const query = `{
    finance(registration: "${registration}", type: "${type}", ${
      deposit ? `deposit: ${deposit},` : ''
    } annualMileage: ${annualMileage}, termLength: ${termLength}) {
      monthlyPayment,
      carPrice,
      depositAmount,
      maxDepositAmount,
      adminFee,
      balanceToFinance,
      firstPayment,
      numberOfPayments,
      finalPayment,
      amountPayable,
      interestCharges,
      interestApr,
      interestRate,
      optionFee,
      lender,
      lenderDisplayName,
      terms,
      financeCalculatorError
    }
  }`
    return request(`${process.env.GATSBY_API_URL}/finance-calculator`, query)
  },
  { maxAge: 300e3 }
)

const floatPrecisionAlgorithm = (precision) => ({
  getPosition: (value, min, max) => ((value - min) / (max - min)) * 100,
  getValue: (pos, min, max) => {
    const decimal = pos / 100
    if (pos === 0) return min
    if (pos === 100) return max
    return parseFloat(((max - min) * decimal + min).toFixed(precision))
  }
})

class ContentFinanceTabs extends React.PureComponent {
  constructor(props) {
    super(props)
    this._updateDepositValues = this._updateDepositValues.bind(this)
    this._updateRepaymentValues = this._updateRepaymentValues.bind(this)
    this._updateMileageValues = this._updateMileageValues.bind(this)
    this._handlePreferenceChange = this._handlePreferenceChange.bind(this)
    this.handleCompareDefaults = this.handleCompareDefaults.bind(this)
    this.handleReset = this.handleReset.bind(this)
    this.handleSliderChange = debounce(this.handleSliderChange.bind(this), 200)
    this.handleModalClose = this.handleModalClose.bind(this)
    this.handleModalVideoToggle = this.handleModalVideoToggle.bind(this)

    const { summary, category, resetFn, showPcp } = props
    const repaymentPreference = 1
    const paymentState = (type) => ({
      deposit: {
        snapPoints: this._getSliderSnapPoints(this.range.deposit),
        amount: {
          values: [this.range.deposit.max],
          min: this.range.deposit.min,
          max: this.range.deposit.max,
          initialValues: []
        }
      },
      repayment: {
        snapPoints: this._getSliderSnapPoints(this.range.repayment),
        amount: {
          values: [this.repaymentValues[repaymentPreference][type].default],
          min: this.range.repayment.min,
          max: this.range.repayment.max
        }
      },
      mileage: {
        snapPoints: this._getSliderSnapPoints(this.range.mileage),
        amount: {
          values: [this.range.mileage.default],
          min: this.range.mileage.min,
          max: this.range.mileage.max
        }
      }
    })

    summary.carPrice.label =
      category.charAt(0).toUpperCase() + category.substring(1) + ' Price'

    // This is a hack to pass the handleReset up to the component above
    // as the state for the finance data is stored within this component
    resetFn(this.handleReset)

    this.repaymentValues = [
      {
        hp: {
          label: 'Year',
          min: 1,
          max: 5,
          factor: 0.5,
          default: 4
        },
        pcp: {
          label: 'Year',
          min: 1,
          max: 4,
          factor: 0.5,
          default: 3
        }
      },
      {
        hp: {
          label: 'Month',
          min: 12,
          max: 60,
          factor: 1,
          default: 48
        },
        pcp: {
          label: 'Month',
          min: 12,
          max: 48,
          default: 42,
          factor: 1
        }
      }
    ]

    this.tabTitle = ['hp']
    if (showPcp) this.tabTitle.unshift('pcp')

    // deposit values are now dynamic, rheostat is not handling updated maximum deposit values correctly
    // a default maximum that will never be reached is set
    // deposits can be set to any amount to the maximum depending on the query.
    this.range = {
      deposit: { min: 0, max: 100000, factor: 5 },
      repayment: this.repaymentValues[1][this.tabTitle[0]],
      mileage: { min: 6000, max: 24000, factor: 1000, default: 8000 }
    }

    this.state = {
      tabIndex: 0,
      initialRenderLoading: true,
      resetEnabled: false,
      preferences: {
        repayment: repaymentPreference
      },
      hp: {
        ...paymentState('hp'),
        slidersChanged: false
      },
      pcp: {
        ...paymentState('pcp'),
        slidersChanged: false
      },
      summary,
      quoteError: false,
      financeCalculatorError: '',
      slidersActive: true,
      summaryActive: false,
      modalVideoActive: false,
      awaitingSearch: false
    }
  }

  componentDidMount() {
    const type = this.props.showPcp ? 'pcp' : 'hp'
    const state = this.state[type]
    state.deposit.amount.values = []
    this.handleSliderChange(this.state, false)
  }

  handleCompareDefaults(type, state) {
    const { deposit, repayment, mileage } = state[type]
    if (
      deposit.amount.values[0] !== deposit.amount.initialValues[0] ||
      repayment.amount.values[0] !== this.range.repayment.default ||
      mileage.amount.values[0] !== this.range.mileage.default
    ) {
      return true
    }
    return false
  }

  handleReset(type) {
    trackEvent('FinanceCalculator', 'Reset', `${type.toUpperCase()}`)
    const localState = this.state
    const state = localState[type]
    localState.preferences.repayment = 1
    this.range.repayment = this.repaymentValues[1][type]

    state.resetEnabled = false
    state.deposit.amount.values = state.deposit.amount.initialValues
    state.repayment.amount.values = [this.range.repayment.default]
    state.mileage.amount.values = [this.range.mileage.default]
    this.handleSliderChange(localState)
  }

  async handleSliderChange(state, hasUserChangedSlider = true) {
    const type = this.tabTitle[this.state.tabIndex]
    if (state.slidersActive) {
      try {
        this.setState({ awaitingSearch: true })
        const quoteType = this.tabTitle[state.tabIndex].toUpperCase()
        const repaymentInMonths =
          state.preferences.repayment === 0
            ? state[type].repayment.amount.values * 12
            : state[type].repayment.amount.values
        const data = await search(
          quoteType,
          this.props.registration,
          state[type].slidersChanged ? state[type].deposit.amount.values : 0,
          state[type].mileage.amount.values,
          repaymentInMonths
        )
        const currentSummary = { ...this.state.summary }

        // Loop finance data and update finance summary
        Object.keys(data.finance[0]).map((key) => {
          let value = data.finance[0][key]
          const summaryObject = currentSummary[key]

          if (summaryObject) {
            // Final Payment has a supplement item which needs updating based on the optionFee
            // Could be done better, however no link between finalPayment and optionFee currently
            if (key === 'finalPayment') {
              let supplement = summaryObject.supplement[0]
              supplement.value = data.finance[0].optionFee
            }

            // Ensure if value is a currency and has a decimal place, it has 2 (e.g. 15.1 to 15.10)
            if (summaryObject.prefix === '£' && value) {
              value = value.toString()
              const decimalIndex = value.indexOf('.') + 1
              if (value.length - decimalIndex < 2 && value !== '0')
                value = value + '0'
            }

            summaryObject.value = value
          }
        })

        const depositAmountValues =
          data && data.finance && data.finance.length > 0
            ? [data.finance[0].depositAmount]
            : []
        this.range.deposit.max = data.finance[0].maxDepositAmount

        const newState = {
          initialRenderLoading: false,
          quoteError: data.finance[0].financeCalculatorError,
          financeCalculatorError: false,
          summary: currentSummary,
          lender: data.finance[0].lender
            ? {
                id: data.finance[0].lender,
                name: data.finance[0].lenderDisplayName,
                terms: data.finance[0].terms
              }
            : undefined,
          [type]: {
            ...state[type],
            deposit: {
              ...state[type].deposit,
              amount: {
                ...state[type].deposit.amount,
                values: depositAmountValues,
                max: this.range.deposit.max,
                initialValues:
                  this.state[type].deposit.amount.initialValues.length > 0
                    ? this.state[type].deposit.amount.initialValues
                    : depositAmountValues
              }
            },
            slidersChanged: hasUserChangedSlider
              ? true
              : this.state[type].slidersChanged
          },
          slidersActive: state.slidersActive,
          summaryActive: true,
          awaitingSearch: false
        }

        newState.resetEnabled = this.handleCompareDefaults(type, newState)
        this.setState(newState)
      } catch (e) {
        debug(e)
        // this.range.deposit.max = 0
        this.setState(() => ({
          quoteError: true,
          initialRenderLoading: false,
          slidersActive: false,
          summaryActive: true,
          awaitingSearch: false
        }))
      }
    }
  }

  _getSliderSnapPoints(_range) {
    let _snapPoint = 0
    let _snapPoints = []
    while (_snapPoint <= _range.max) {
      _snapPoints.push(_snapPoint)
      _snapPoint = _snapPoint + _range.factor
    }
    return _snapPoints
  }

  _updateDepositValues(_sliderState) {
    const type = this.tabTitle[this.state.tabIndex]

    if (this.state.quoteError) return // if quote error, values should not be updated
    trackEvent(
      'FinanceCalculator',
      'Deposit',
      `${type.toUpperCase()}`,
      _sliderState.values[0]
    )
    const newState = { ...this.state }
    newState[type].deposit.amount.values = _sliderState.values
    newState.slidersActive = true

    this.setState((prevState) => ({
      [type]: {
        ...prevState[type],
        deposit: {
          ...prevState[type].deposit,
          amount: {
            ...prevState[type].deposit.amount,
            values: _sliderState.values
          }
        }
      }
    }))

    this.handleSliderChange(newState)
  }

  _updateRepaymentValues(_sliderState, state) {
    const type = this.tabTitle[this.state.tabIndex]

    if (this.state.quoteError) return // if quote error, values should not be updated
    trackEvent(
      'FinanceCalculator',
      'Term',
      `${type.toUpperCase()}`,
      _sliderState.values[0]
    )
    state = state || { ...this.state }
    state[type].repayment.amount.values = _sliderState.values.map((value) =>
      this.state.preferences.repayment === 0
        ? parseFloat(value).toFixed(1)
        : value
    )

    this.setState((prevState) => ({
      [type]: {
        ...prevState[type],
        repayment: {
          ...prevState[type].repayment,
          snapPoints: this._getSliderSnapPoints(this.range.repayment),
          amount: {
            ...prevState[type].repayment.amount,
            values: _sliderState.values
          }
        }
      }
    }))
    this.handleSliderChange(state)
  }

  _updateMileageValues(_sliderState) {
    const type = this.tabTitle[this.state.tabIndex]

    if (this.state.quoteError) return // if quote error, values should not be updated
    trackEvent(
      'FinanceCalculator',
      'mileage',
      `${type.toUpperCase()}`,
      _sliderState.values[0]
    )
    const newState = { ...this.state }
    newState[type].mileage.amount.values = _sliderState.values

    this.setState((prevState) => ({
      [type]: {
        ...prevState[type],
        mileage: {
          ...prevState[type].mileage,
          amount: {
            ...prevState[type].mileage.amount,
            values: _sliderState.values
          }
        }
      }
    }))

    this.handleSliderChange(newState)
  }

  _handlePreferenceChange() {
    const type = this.tabTitle[this.state.tabIndex]

    if (this.state.quoteError) return // if quote error, values should not be updated
    const repaymentPreferenceIndex =
      this.state.preferences.repayment === 0 ? 1 : 0
    this.range.repayment = this.repaymentValues[repaymentPreferenceIndex][type]

    // Convert value between months/years
    const currentValue = this.state[type].repayment.amount.values[0]

    let reformattedValue = [
      repaymentPreferenceIndex === 0 ? currentValue / 12 : currentValue * 12
    ]

    if (repaymentPreferenceIndex === 0) {
      reformattedValue = [Math.round(reformattedValue[0] * 2) / 2]
    }

    const newState = { ...this.state }
    newState.preferences.repayment = repaymentPreferenceIndex

    this.setState((prevState) => ({
      preferences: {
        ...prevState.preferences,
        repayment: repaymentPreferenceIndex
      }
    }))

    this._updateRepaymentValues(
      {
        max: this.range.repayment.max,
        min: this.range.repayment.min,
        values: [reformattedValue]
      },
      newState
    )
  }

  _limitValue(value, range) {
    let formattedValue = value >= range.max ? range.max : value
    formattedValue = Math.round(formattedValue / range.factor) * range.factor
    formattedValue = formattedValue < range.min ? range.min : formattedValue
    return {
      value: formattedValue.toLocaleString(),
      isAboveOrEqualToMax: value >= range.max
    }
  }

  // PCP and HP have different max repayment ranges, update ranges and values (if required) when finance plans are changed
  _getRepaymentValue(_tabIndex) {
    const repaymentPreferenceIndex = this.state.preferences.repayment
    const type = this.tabTitle[_tabIndex]
    const repaymentValues = this.repaymentValues[repaymentPreferenceIndex][type]

    this.range.repayment = repaymentValues

    let repaymentValue = [0]
    if (!this.state[type].slidersChanged) {
      repaymentValue = [repaymentValues.default]
    } else {
      repaymentValue =
        this.state[type].repayment.amount.values > repaymentValues.max
          ? [repaymentValues.max]
          : this.state[type].repayment.amount.values
    }

    return repaymentValue
  }

  _onTabSelect(_tabIndex) {
    const type = this.tabTitle[_tabIndex]
    this.props.onTabChange(type)
    const repaymentValue = this._getRepaymentValue(_tabIndex)

    const newState = {
      ...this.state,
      slidersActive: true
    } // Set sliders to true so finance calculation can be attempted

    newState.tabIndex = _tabIndex
    this.setState((prevState) => ({
      tabIndex: _tabIndex,
      slidersActive: false,
      [type]: {
        ...prevState[type],
        repayment: {
          ...prevState[type].repayment,
          snapPoints: this._getSliderSnapPoints(this.range.repayment),
          amount: {
            ...prevState[type].repayment.amount,
            values: repaymentValue
          }
        }
      }
    }))
    this.handleSliderChange(newState, false)
  }

  handleModalClose() {
    this.setState({ modalVideoActive: false })
  }

  handleModalVideoToggle() {
    this.setState({ modalVideoActive: !this.state.modalVideoActive })
  }

  componentDidUpdate() {
    const activeTab = this.props.activeTab
    const tabIndex = this.tabTitle.indexOf(activeTab)

    if (tabIndex !== this.state.tabIndex && tabIndex !== -1) {
      this._onTabSelect(tabIndex)
    }
  }

  render() {
    const lenders = this.state.lender ? [this.state.lender] : undefined
    const displayedTerms = lenders && lenders.length && lenders[0].terms

    return (
      <ContentSection id="finance-calculator-top" key="finance-calculator-top">
        <ContentSectionHeader title="Calculate your finance" reversed>
          <p>
            {this.state.financeCalculatorError
              ? `We’re unable to generate a finance quote online for this
                    vehicle at the moment, sorry. Please contact the team who
                    can assist you over phone/chat/email or in store.`
              : `Our flexible finance options are quick and convenient. You can shop
            and finance directly from this website, where we have detailed
            information about the finance options that are available. Use this
            finance calculator to find the perfect agreement to meet your
            budget.`}
          </p>
        </ContentSectionHeader>
        {!this.state.financeCalculatorError && (
          <Tabs
            selectedIndex={this.state.tabIndex}
            onSelect={(tabIndex) => this._onTabSelect(tabIndex)}
            id="finance-calculator"
          >
            <TabWrapper mx="auto" mb={[25, null, 100]}>
              <Header>
                <TabList>
                  {this.props.showPcp && (
                    <Tab title="PCP">
                      <TabLabel>Personal Contract Plan</TabLabel>
                    </Tab>
                  )}
                  <Tab title="HP">
                    <TabLabel>Hire purchase</TabLabel>
                  </Tab>
                </TabList>
              </Header>
              <Box p={3}>
                {this.props.showPcp && (
                  <TabPanel>
                    <Form>
                      <Flex flexWrap="wrap" width={2 / 2} px={[0, 10]} py={20}>
                        <Box
                          width={[12 / 12, 6 / 12]}
                          px={[0, '3%']}
                          pb={[2, 3, 5]}
                        >
                          <Flex
                            px={12}
                            flexDirection="column"
                            pb={[24, 32]}
                            textAlign="left"
                          >
                            <Heading fontSize={[6, 7, 8]}>
                              Personal Contract Plan
                            </Heading>
                            <ContentClickModal
                              clicker={<Link>What is PCP?</Link>}
                              title="What is PCP?"
                              onClose={this.handleModalClose}
                            >
                              <PCP
                                isVideoVisible={this.state.modalVideoActive}
                                onVideoToggle={this.handleModalVideoToggle}
                              />
                            </ContentClickModal>
                          </Flex>
                          {this.state.initialRenderLoading && (
                            <LoadingSpinner />
                          )}
                          {!this.state.initialRenderLoading && (
                            <>
                              <fieldset disabled={!this.state.slidersActive}>
                                <Legend>Deposit Amount</Legend>
                                <RheostatSliderWithDragValue
                                  disabled={!this.state.slidersActive}
                                  slidervalue={`£${
                                    this._limitValue(
                                      this.state.pcp.deposit.amount.values,
                                      this.range.deposit
                                    ).value
                                  }`}
                                  py={20}
                                  px={12}
                                  mt={15}
                                  mb={-10}
                                >
                                  <Rheostat
                                    min={0}
                                    max={this.state.pcp.deposit.amount.max}
                                    values={
                                      this.state.pcp.deposit.amount.values
                                    }
                                    onValuesUpdated={this._updateDepositValues}
                                    snap
                                    snapPoints={
                                      this.state.pcp.deposit.snapPoints
                                    }
                                    disabled={!this.state.slidersActive}
                                  />
                                </RheostatSliderWithDragValue>
                                <Flex px={12}>
                                  <StaticLabel
                                    onClick={() =>
                                      this._updateDepositValues({
                                        max: this.range.deposit.max,
                                        min: this.range.deposit.min,
                                        values: [this.range.deposit.min]
                                      })
                                    }
                                    pointer={this.state.slidersActive}
                                    textAlign="left"
                                    width={1 / 2}
                                  >
                                    <NumberFormat
                                      value={this.range.deposit.min}
                                      displayType={'text'}
                                      thousandSeparator
                                      prefix={'£'}
                                    />
                                  </StaticLabel>
                                  <StaticLabel
                                    onClick={() =>
                                      this._updateDepositValues({
                                        max: this.range.deposit.max,
                                        min: this.range.deposit.min,
                                        values: [this.range.deposit.max]
                                      })
                                    }
                                    pointer={this.state.slidersActive}
                                    textAlign="right"
                                    width={1 / 2}
                                  >
                                    <NumberFormat
                                      value={this.range.deposit.max}
                                      displayType={'text'}
                                      thousandSeparator
                                      prefix={'£'}
                                    />
                                  </StaticLabel>
                                </Flex>
                              </fieldset>

                              <fieldset disabled={!this.state.slidersActive}>
                                <Legend>
                                  PCP Repayment period in{' '}
                                  {this.repaymentValues[
                                    this.state.preferences.repayment
                                  ].pcp.label.toLowerCase()}
                                  s:{' '}
                                  <a
                                    onClick={this._handlePreferenceChange}
                                    href="javascript:void(0)"
                                  >
                                    Show me{' '}
                                    {this.repaymentValues[
                                      Math.abs(
                                        this.state.preferences.repayment * -1 +
                                          1
                                      )
                                    ].pcp.label.toLowerCase()}
                                    s
                                  </a>
                                </Legend>
                                <RheostatSliderWithDragValue
                                  disabled={!this.state.slidersActive}
                                  slidervalue={
                                    this._limitValue(
                                      this.state.pcp.repayment.amount.values,
                                      this.range.repayment
                                    ).value
                                  }
                                  py={20}
                                  px={12}
                                  mt={15}
                                  mb={-10}
                                >
                                  <Rheostat
                                    min={this.range.repayment.min}
                                    max={this.range.repayment.max}
                                    values={
                                      this.state.pcp.repayment.amount.values
                                    }
                                    onValuesUpdated={
                                      this._updateRepaymentValues
                                    }
                                    snap
                                    snapPoints={
                                      this.state.pcp.repayment.snapPoints
                                    }
                                    disabled={!this.state.slidersActive}
                                    {...(this.state.preferences.repayment ===
                                      0 && {
                                      algorithm: floatPrecisionAlgorithm(1)
                                    })}
                                  />
                                </RheostatSliderWithDragValue>
                                <Flex px={12}>
                                  <StaticLabel
                                    onClick={() =>
                                      this._updateRepaymentValues({
                                        max: this.range.repayment.max,
                                        min: this.range.repayment.min,
                                        values: [this.range.repayment.min]
                                      })
                                    }
                                    pointer={this.state.slidersActive}
                                    textAlign="left"
                                    width={1 / 2}
                                    match={
                                      this.range.repayment.min ===
                                      this.state.pcp.repayment.amount.values[0]
                                    }
                                  >
                                    <NumberFormat
                                      value={this.range.repayment.min}
                                      displayType={'text'}
                                      thousandSeparator
                                      suffix={` ${
                                        this.repaymentValues[
                                          this.state.preferences.repayment
                                        ].pcp.label
                                      }${
                                        this.range.repayment.min > 1 ? 's' : ''
                                      }`}
                                    />
                                  </StaticLabel>
                                  <StaticLabel
                                    onClick={() =>
                                      this._updateRepaymentValues({
                                        max: this.range.repayment.max,
                                        min: this.range.repayment.min,
                                        values: [this.range.repayment.max]
                                      })
                                    }
                                    pointer={this.state.slidersActive}
                                    textAlign="right"
                                    width={1 / 2}
                                    match={
                                      this.range.repayment.max ===
                                      this.state.pcp.repayment.amount.values[0]
                                    }
                                  >
                                    <NumberFormat
                                      value={this.range.repayment.max}
                                      displayType={'text'}
                                      thousandSeparator
                                      suffix={` ${
                                        this.repaymentValues[
                                          this.state.preferences.repayment
                                        ].pcp.label
                                      }s`}
                                    />
                                  </StaticLabel>
                                </Flex>
                              </fieldset>

                              <fieldset disabled={!this.state.slidersActive}>
                                <Legend>Annual Mileage</Legend>
                                <RheostatSliderWithDragValue
                                  disabled={!this.state.slidersActive}
                                  slidervalue={
                                    this._limitValue(
                                      this.state.pcp.mileage.amount.values,
                                      this.range.mileage
                                    ).value
                                  }
                                  py={20}
                                  px={12}
                                  mt={15}
                                  mb={-10}
                                >
                                  <Rheostat
                                    min={this.range.mileage.min}
                                    max={this.range.mileage.max}
                                    values={
                                      this.state.pcp.mileage.amount.values
                                    }
                                    onValuesUpdated={this._updateMileageValues}
                                    snap
                                    snapPoints={
                                      this.state.pcp.mileage.snapPoints
                                    }
                                    disabled={!this.state.slidersActive}
                                  />
                                </RheostatSliderWithDragValue>
                                <Flex px={12}>
                                  <StaticLabel
                                    onClick={() =>
                                      this._updateMileageValues({
                                        max: this.range.mileage.max,
                                        min: this.range.mileage.min,
                                        values: [this.range.mileage.min]
                                      })
                                    }
                                    pointer={this.state.slidersActive}
                                    textAlign="left"
                                    width={1 / 2}
                                  >
                                    <NumberFormat
                                      value={this.range.mileage.min}
                                      displayType={'text'}
                                      thousandSeparator
                                    />
                                  </StaticLabel>
                                  <StaticLabel
                                    textAlign="right"
                                    width={1 / 2}
                                    onClick={() =>
                                      this._updateMileageValues({
                                        max: this.range.mileage.max,
                                        min: this.range.mileage.min,
                                        values: [this.range.mileage.max]
                                      })
                                    }
                                    pointer={this.state.slidersActive}
                                  >
                                    <NumberFormat
                                      value={this.range.mileage.max}
                                      displayType={'text'}
                                      thousandSeparator
                                    />
                                  </StaticLabel>
                                </Flex>
                              </fieldset>
                              <ResetButtonWrapper>
                                <ResetButton
                                  active={this.state.resetEnabled}
                                  onClick={() => this.handleReset('pcp')}
                                  disabled={!this.state.resetEnabled}
                                  type="button"
                                >
                                  Reset to defaults
                                </ResetButton>
                              </ResetButtonWrapper>
                              <Legal terms={displayedTerms} />
                            </>
                          )}
                        </Box>
                        <Box
                          width={[12 / 12, 6 / 12]}
                          pl={[0, '5%', '10%']}
                          pb={[2, 3, 5]}
                        >
                          <ContentFinanceSummary
                            active={this.state.summaryActive}
                            summarylist={this.state.summary}
                            lenders={
                              this.state.lender
                                ? [this.state.lender]
                                : undefined
                            }
                            error={this.state.quoteError}
                            contentid="pcp"
                            phoneNumber={this.props.phoneNumber}
                          />
                        </Box>
                      </Flex>
                    </Form>
                  </TabPanel>
                )}

                {/* HP TAB */}

                <TabPanel>
                  <Form>
                    <Flex flexWrap="wrap" width={2 / 2} px={[0, 10]} py={20}>
                      <Flex
                        flexDirection="column"
                        width={[12 / 12, 6 / 12]}
                        px={[0, '3%']}
                        pb={[2, 3, 4]}
                      >
                        <Flex
                          px={12}
                          flexDirection="column"
                          pb={[24, 32]}
                          textAlign="left"
                        >
                          <Heading fontSize={[6, 7, 8]}>Hire Purchase</Heading>
                          <ContentClickModal
                            clicker={<Link>What is HP?</Link>}
                            title="What is HP?"
                            onClose={this.handleModalClose}
                          >
                            <HP
                              isVideoVisible={this.state.modalVideoActive}
                              onVideoToggle={this.handleModalVideoToggle}
                            />
                          </ContentClickModal>
                        </Flex>
                        <fieldset disabled={!this.state.slidersActive}>
                          <Legend>Deposit Amount</Legend>
                          <RheostatSliderWithDragValue
                            disabled={!this.state.slidersActive}
                            slidervalue={`£${
                              this._limitValue(
                                this.state.hp.deposit.amount.values,
                                this.range.deposit
                              ).value
                            }`}
                            py={20}
                            px={12}
                            mt={15}
                            mb={-10}
                          >
                            <Rheostat
                              min={this.range.deposit.min}
                              max={this.range.deposit.max}
                              values={this.state.hp.deposit.amount.values}
                              onValuesUpdated={this._updateDepositValues}
                              snap
                              snapPoints={this.state.hp.deposit.snapPoints}
                              disabled={!this.state.slidersActive}
                            />
                          </RheostatSliderWithDragValue>
                          <Flex px={12}>
                            <StaticLabel
                              onClick={() =>
                                this._updateDepositValues({
                                  max: this.range.deposit.max,
                                  min: this.range.deposit.min,
                                  values: [this.range.deposit.min]
                                })
                              }
                              pointer={this.state.slidersActive}
                              textAlign="left"
                              width={1 / 2}
                            >
                              <NumberFormat
                                value={this.range.deposit.min}
                                displayType={'text'}
                                thousandSeparator
                                prefix={'£'}
                              />
                            </StaticLabel>
                            <StaticLabel
                              onClick={() =>
                                this._updateDepositValues({
                                  max: this.range.deposit.max,
                                  min: this.range.deposit.min,
                                  values: [this.range.deposit.max]
                                })
                              }
                              pointer={this.state.slidersActive}
                              textAlign="right"
                              width={1 / 2}
                            >
                              <NumberFormat
                                value={this.range.deposit.max}
                                displayType={'text'}
                                thousandSeparator
                                prefix={'£'}
                              />
                            </StaticLabel>
                          </Flex>
                        </fieldset>

                        <fieldset disabled={!this.state.slidersActive}>
                          <Legend>
                            HP Repayment period in{' '}
                            {this.repaymentValues[
                              this.state.preferences.repayment
                            ].hp.label.toLowerCase()}
                            s{': '}
                            <a
                              onClick={this._handlePreferenceChange}
                              href="javascript:void(0)"
                            >
                              Show me{' '}
                              {
                                this.repaymentValues[
                                  Math.abs(
                                    this.state.preferences.repayment * -1 + 1
                                  )
                                ].hp.label
                              }
                              s
                            </a>
                          </Legend>
                          <RheostatSliderWithDragValue
                            disabled={!this.state.slidersActive}
                            slidervalue={`${
                              this._limitValue(
                                this.state.hp.repayment.amount.values,
                                this.range.repayment
                              ).value
                            }`}
                            py={20}
                            px={12}
                            mt={15}
                            mb={-10}
                          >
                            <Rheostat
                              min={this.range.repayment.min}
                              max={this.range.repayment.max}
                              values={this.state.hp.repayment.amount.values}
                              onValuesUpdated={this._updateRepaymentValues}
                              snap
                              snapPoints={this.state.hp.repayment.snapPoints}
                              disabled={!this.state.slidersActive}
                            />
                          </RheostatSliderWithDragValue>
                          <Flex px={12}>
                            <StaticLabel
                              onClick={() =>
                                this._updateRepaymentValues({
                                  max: this.range.repayment.max,
                                  min: this.range.repayment.min,
                                  values: [this.range.repayment.min]
                                })
                              }
                              pointer={this.state.slidersActive}
                              textAlign="left"
                              width={1 / 2}
                              match={
                                this.range.repayment.min ===
                                this.state.hp.repayment.amount.values[0]
                              }
                            >
                              <NumberFormat
                                value={this.range.repayment.min}
                                displayType={'text'}
                                thousandSeparator
                                suffix={` ${
                                  this.repaymentValues[
                                    this.state.preferences.repayment
                                  ].hp.label
                                }${this.range.repayment.min > 1 ? 's' : ''}`}
                              />
                            </StaticLabel>
                            <StaticLabel
                              onClick={() =>
                                this._updateRepaymentValues({
                                  max: this.range.repayment.max,
                                  min: this.range.repayment.min,
                                  values: [this.range.repayment.max]
                                })
                              }
                              pointer={this.state.slidersActive}
                              textAlign="right"
                              width={1 / 2}
                              match={
                                this.range.repayment.max ===
                                this.state.hp.repayment.amount.values[0]
                              }
                            >
                              <NumberFormat
                                value={this.range.repayment.max}
                                displayType={'text'}
                                thousandSeparator
                                suffix={` ${
                                  this.repaymentValues[
                                    this.state.preferences.repayment
                                  ].hp.label
                                }s`}
                              />
                            </StaticLabel>
                          </Flex>
                        </fieldset>
                        <ResetButtonWrapper>
                          <ResetButton
                            active={this.state.resetEnabled}
                            onClick={() => this.handleReset('hp')}
                            disabled={!this.state.resetEnabled}
                            type="button"
                          >
                            Reset to defaults
                          </ResetButton>
                        </ResetButtonWrapper>
                        <Legal terms={displayedTerms} />
                      </Flex>
                      <Box
                        width={[12 / 12, 6 / 12]}
                        pl={[0, '5%', '10%']}
                        pb={[2, 3, 4]}
                      >
                        <ContentFinanceSummary
                          active={this.state.summaryActive}
                          summarylist={this.state.summary}
                          loading={
                            !this.state.initialRenderLoading &&
                            this.state.awaitingSearch
                          }
                          lenders={
                            this.state.lender ? [this.state.lender] : undefined
                          }
                          error={this.state.quoteError}
                          contentid="hp"
                          phoneNumber={this.props.phoneNumber}
                        />
                      </Box>
                    </Flex>
                  </Form>
                </TabPanel>
              </Box>
            </TabWrapper>
          </Tabs>
        )}
      </ContentSection>
    )
  }
}

ContentFinanceTabs.defaultProps = {
  phoneNumber: '0330 096 3851',
  summary: {
    monthlyPayment: {
      label: 'Monthly Payments',
      value: 0,
      prefix: '£',
      suffix: '',
      supplement: []
    },
    carPrice: {
      label: 'Car Price',
      value: 0,
      prefix: '£',
      suffix: '',
      supplement: []
    },
    depositAmount: {
      label: 'Deposit Amount',
      value: 0,
      prefix: '£',
      suffix: '',
      supplement: []
    },
    adminFee: {
      label: 'Admin Fee',
      value: 0,
      prefix: '£',
      suffix: '',
      supplement: []
    },
    balanceToFinance: {
      label: 'Balance to Finance',
      value: 0,
      prefix: '£',
      suffix: '',
      supplement: []
    },
    numberOfPayments: {
      label: 'Term Length',
      value: 0,
      prefix: '',
      suffix: ' Months',
      supplement: []
    },
    firstPayment: {
      label: 'First Payment',
      value: 0,
      prefix: '£',
      suffix: '',
      supplement: []
    },
    finalPayment: {
      label: 'Optional Final Payment',
      value: 0,
      prefix: '£',
      suffix: '',
      supplement: [
        {
          label: 'Includes Purchase Fee',
          value: 0,
          prefix: '£',
          suffix: '',
          supplement: []
        }
      ]
    },
    amountPayable: {
      label: 'Total Amount Payable',
      value: 0,
      prefix: '£',
      suffix: '',
      supplement: []
    },
    interestCharges: {
      label: 'Interest Charges',
      value: 0,
      prefix: '£',
      suffix: '',
      supplement: []
    },
    interestApr: {
      label: 'Representative APR',
      value: 0,
      prefix: '',
      suffix: '%',
      supplement: []
    },
    interestRate: {
      label: 'Interest Rate (fixed)',
      value: 0,
      prefix: '',
      suffix: '%',
      supplement: []
    }
  },
  showPcp: true
}

ContentFinanceTabs.propTypes = {
  registration: string,
  phoneNumber: string
}

export default ContentFinanceTabs
